/**
 * InsuranceTab Component
 *
 * Tab content for managing insurance information.
 * Based on SETTINGS_FUNCTIONAL_SPEC.md Section 1.5 "Insurance Tab"
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  Check,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import type {
  CaseFile,
  InsuranceInfo,
  UpdateCaseFileResponse,
} from '../../types/settings';

interface InsuranceTabProps {
  caseFile: CaseFile;
  onUpdated: () => void;
}

export function InsuranceTab({ caseFile, onUpdated }: InsuranceTabProps): JSX.Element {
  // Form state
  const [provider, setProvider] = useState(caseFile.insuranceInfo?.provider || '');
  const [planType, setPlanType] = useState(caseFile.insuranceInfo?.planType || '');
  const [policyNumber, setPolicyNumber] = useState(caseFile.insuranceInfo?.policyNumber || '');
  const [groupNumber, setGroupNumber] = useState(caseFile.insuranceInfo?.groupNumber || '');
  const [subscriberId, setSubscriberId] = useState(caseFile.insuranceInfo?.subscriberId || '');

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes
  useEffect(() => {
    const insuranceInfo = caseFile.insuranceInfo || {};
    const changed =
      provider !== (insuranceInfo.provider || '') ||
      planType !== (insuranceInfo.planType || '') ||
      policyNumber !== (insuranceInfo.policyNumber || '') ||
      groupNumber !== (insuranceInfo.groupNumber || '') ||
      subscriberId !== (insuranceInfo.subscriberId || '');

    setHasChanges(changed);
  }, [provider, planType, policyNumber, groupNumber, subscriberId, caseFile.insuranceInfo]);

  // Reset form when caseFile changes
  useEffect(() => {
    setProvider(caseFile.insuranceInfo?.provider || '');
    setPlanType(caseFile.insuranceInfo?.planType || '');
    setPolicyNumber(caseFile.insuranceInfo?.policyNumber || '');
    setGroupNumber(caseFile.insuranceInfo?.groupNumber || '');
    setSubscriberId(caseFile.insuranceInfo?.subscriberId || '');
  }, [caseFile]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updateCaseFile = httpsCallable<
        {
          caseFileId: string;
          updates: {
            insuranceInfo?: InsuranceInfo;
          };
        },
        UpdateCaseFileResponse
      >(functions, 'updateCaseFile');

      const result = await updateCaseFile({
        caseFileId: caseFile.caseFileId,
        updates: {
          insuranceInfo: {
            provider: provider || undefined,
            planType: planType || undefined,
            policyNumber: policyNumber || undefined,
            groupNumber: groupNumber || undefined,
            subscriberId: subscriberId || undefined,
          },
        },
      });

      if (result.data.success) {
        setSuccess('Insurance information saved successfully');
        setHasChanges(false);
        onUpdated();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.data.error || 'Failed to save insurance information');
      }
    } catch (err) {
      console.error('Error saving insurance info:', err);
      setError('Failed to save insurance information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Success/Error Messages */}
      {success && (
        <div className="alert alert-success mb-6 flex items-center gap-2" role="status" aria-live="polite">
          <Check className="w-5 h-5" aria-hidden="true" />
          {success}
        </div>
      )}
      {error && (
        <div className="alert alert-error mb-6 flex items-center gap-2" role="alert">
          <AlertCircle className="w-5 h-5" aria-hidden="true" />
          {error}
        </div>
      )}

      {/* Primary Insurance Card */}
      <div className="card mb-6">
        <div className="p-6">
          <h3 className="text-lg font-bold text-ink mb-5">Primary Insurance</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="ins-provider" className="label">Insurance Provider</label>
              <input
                id="ins-provider"
                type="text"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                placeholder="e.g., Blue Cross Blue Shield"
                className="input"
              />
            </div>

            <div>
              <label htmlFor="ins-plan-type" className="label">Plan Type</label>
              <input
                id="ins-plan-type"
                type="text"
                value={planType}
                onChange={(e) => setPlanType(e.target.value)}
                placeholder="e.g., PPO, HMO"
                className="input"
              />
            </div>

            <div>
              <label htmlFor="ins-policy-number" className="label">Policy Number</label>
              <input
                id="ins-policy-number"
                type="text"
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                placeholder="Enter policy number"
                className="input"
              />
            </div>

            <div>
              <label htmlFor="ins-group-number" className="label">Group Number</label>
              <input
                id="ins-group-number"
                type="text"
                value={groupNumber}
                onChange={(e) => setGroupNumber(e.target.value)}
                placeholder="Enter group number"
                className="input"
              />
            </div>

            <div>
              <label htmlFor="ins-subscriber-id" className="label">Subscriber ID</label>
              <input
                id="ins-subscriber-id"
                type="text"
                value={subscriberId}
                onChange={(e) => setSubscriberId(e.target.value)}
                placeholder="Enter subscriber ID"
                className="input"
              />
            </div>

            <div></div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="btn-primary flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </>
  );
}

export default InsuranceTab;
